import { zodResolver } from "@hookform/resolvers/zod";
import {
  useEnableMFaMutation,
  useGenerateMfaqrCodeQuery,
} from "gql/generated.graphql";
import { useContext, useState } from "react";
import { useForm } from "react-hook-form";
import { FiChevronLeft, FiLoader } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import { ApolloError } from "@apollo/client";
import { localStorage, ViewerContext } from "dash-ui";
import { RoutesPath } from "flow-components/routes/routes";

const confirmMfaCodeFormSchema = z.object({
  code: z.string().min(1, "Code is required."),
});

type ConfirmMfaCodeFormData = z.infer<typeof confirmMfaCodeFormSchema>;

export function EnableMfaForm() {
  // React states
  const [step, setStep] = useState(0);

  // Queries and Mutations
  const generateQrCodeQuery = useGenerateMfaqrCodeQuery();
  const [enableMFaMutation] = useEnableMFaMutation();

  // Hooks
  const navigate = useNavigate();
  const confirmMfaCodeForm = useForm<ConfirmMfaCodeFormData>({
    resolver: zodResolver(confirmMfaCodeFormSchema),
  });
  const { setTokens } = useContext(ViewerContext);

  // Handle next step
  function nextStep() {
    setStep(1);
  }

  // Handle confirm code
  async function confirmMfaCode(data: ConfirmMfaCodeFormData) {
    await enableMFaMutation({
      variables: { token: data.code },
    })
      .then((result) => {
        const accessToken = result.data?.enableMfa.accessToken;
        const refreshToken = result.data?.enableMfa.refreshToken;
        const refreshTokenExpiry = result.data?.enableMfa.refreshTokenExpiry;

        if (!accessToken || !refreshToken) {
          confirmMfaCodeForm.setError("code", {
            message:
              "Error: Access token not provided. Please contact support.",
          });
          return;
        }

        localStorage.setAccessToken(accessToken);
        localStorage.setRefreshToken(refreshToken);
        localStorage.setRefreshTokenExpiry(refreshTokenExpiry || "");

        setTokens?.({
          accessToken: accessToken,
          refreshToken: refreshToken,
          refreshTokenExpiry: refreshTokenExpiry,
        });
        setStep(2);
      })
      .catch((error: ApolloError) => {
        if (error instanceof ApolloError) {
          if (error.message === "Unauthorized")
            return navigate(RoutesPath.SignIn);
        }
        confirmMfaCodeForm.setError("code", {
          message: (error as Error).message,
        });
      });
  }

  // Handle done
  function done() {
    navigate(RoutesPath.Properties);
  }

  // Show error if QR Code are not available
  if (
    !generateQrCodeQuery.loading &&
    !generateQrCodeQuery.data?.generateMfaQrCode.qrCode
  ) {
    return (
      <div className="flex flex-col items-center justify-center w-full h-full">
        <div className="flex flex-col w-full max-w-80 p-4">
          <p className="bg-red-50 border border-red-600 px-4 py-2">
            Something went wrong while generating the QR code for MFA
            activation. Please contact the support.
          </p>
        </div>
      </div>
    );
  }

  // HTML Component
  return (
    <>
      {step === 0 && (
        <div className="flex flex-col items-center justify-center h-full w-full">
          <div className="flex flex-col w-full max-w-80 p-4">
            <h1 className="text-2xl font-bold font-[montserrat]">Enable MFA</h1>
            <p className="text-slate-500 mt-1">
              Please enable MFA to proceed. Scan the QR code with your
              authenticator app.
            </p>
            {generateQrCodeQuery.loading && (
              <FiLoader className="mx-auto animate-spin" />
            )}
            <img
              src={generateQrCodeQuery.data?.generateMfaQrCode.qrCode}
              className="bg-sky-700 w-full rounded mt-4 mx-auto object-contain"
            />
            <button
              className="bg-sky-600 text-white rounded h-10 px-4 mt-4"
              onClick={nextStep}
            >
              Continue
            </button>
          </div>
        </div>
      )}

      {step === 1 && (
        <div className="flex flex-col items-center justify-center h-full w-full">
          <form
            className="flex flex-col w-full max-w-80 p-4"
            onSubmit={confirmMfaCodeForm.handleSubmit(confirmMfaCode)}
          >
            <h1 className="text-2xl font-bold font-[montserrat]">Enable MFA</h1>
            <p className="text-slate-500 mt-1">
              Now enter the code that appears in your authenticator app before
              it expires.
            </p>
            <label className="text-sm text-slate-500 mt-4">Code</label>
            <input
              className={`h-10 border rounded px-4 ${
                confirmMfaCodeForm.formState.errors.code
                  ? "border-red-600 bg-red-50"
                  : "border-slate-300"
              }`}
              {...confirmMfaCodeForm.register("code")}
              placeholder="123456"
              autoFocus
            />
            <span className="text-red-600 text-sm">
              {confirmMfaCodeForm.formState.errors.code?.message}
            </span>
            <button
              type="submit"
              className="h-10 px-4 rounded bg-sky-600 text-white mt-4"
            >
              Confirm
            </button>
            <button
              className="flex items-center w-max text-sky-700 underline underline-offset-4 mt-8"
              type="reset"
              onClick={() => setStep(0)}
            >
              <FiChevronLeft /> Back
            </button>
          </form>
        </div>
      )}

      {step === 2 && (
        <div className="flex flex-col items-center justify-center w-full h-full">
          <div className="flex flex-col w-full max-w-80 p-4">
            <h1 className="text-2xl font-bold font-[montserrat]">Enable MFA</h1>
            <p className="text-slate-500 mt-1">
              Done! Now MFA will be requested every time you log into the
              application.
            </p>
            <button
              className="bg-sky-600 text-white rounded px-4 h-10 mt-8"
              onClick={done}
            >
              Done
            </button>
          </div>
        </div>
      )}
    </>
  );
}
